import type { StorageIntegration, StorageIntegrationProvider } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

interface GetAuthUriParams {
  provider: StorageIntegrationProvider
  returnUrl?: string
  storageIntegrationId?: string
  egnyteDomain?: string
  context?: string
}

interface GetAuthUriResponse {
  auth_uri: string
}

interface BrowseParams {
  integrationId: string
  folderId?: string
  signal?: AbortSignal
}

interface PickerBrowseParams extends BrowseParams {
  query?: string
}

interface FileInfo {
  id: string
  name: string
  type: "file" | "folder"
  mime_type: string
  size?: string
  modified_at?: string
  web_url?: string
}

interface ParentFolder {
  id: string
  name: string
  web_url?: string
}

interface BrowseResponse {
  files: FileInfo[]
  parent_folder?: ParentFolder
}

export function getStorageAuthUri(context: ApiContext, params: GetAuthUriParams) {
  const searchParams = new URLSearchParams({
    provider: params.provider,
  })

  if (params.returnUrl) {
    searchParams.append("return_url", params.returnUrl)
  }

  if (params.storageIntegrationId) {
    searchParams.append("storage_integration_id", params.storageIntegrationId)
  }

  if (params.egnyteDomain) {
    searchParams.append("egnyte_domain", params.egnyteDomain)
  }

  if (params.context) {
    searchParams.append("context", params.context)
  }

  return apiContextFetch<GetAuthUriResponse>(context, `storage_integrations/get_auth_uri?${searchParams.toString()}`)
}

export function fetchStorageIntegrations(context: ApiContext) {
  return apiContextFetch<{ storage_integrations: StorageIntegration[] }>(context, `storage_integrations`)
}

export function fetchStorageIntegration(context: ApiContext, id: string) {
  return apiContextFetch<StorageIntegration>(context, `storage_integrations/${id}`)
}

export function updateStorageIntegration(context: ApiContext, id: string, data: { is_active: boolean }) {
  return apiContextFetch<StorageIntegration>(context, `storage_integrations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  })
}

export function deleteStorageIntegration(context: ApiContext, id: string) {
  return apiContextFetch(context, `storage_integrations/${id}`, {
    method: "DELETE",
  })
}

export function browseStorage(context: ApiContext, params: BrowseParams) {
  const searchParams = new URLSearchParams()
  if (params.folderId) {
    searchParams.append("folder_id", params.folderId)
  }

  const query = searchParams.toString()
  const url = `storage_integrations/${params.integrationId}/browse${query ? `?${query}` : ""}`

  return apiContextFetch<BrowseResponse>(context, url, { signal: params.signal })
}

// Picker browse uses a relaxed policy: any book user can browse to pick a link,
// unlike `browse` which requires folder-management permission.
// When `query` is present the provider searches by name instead of listing a folder.
export function browseStorageForPicker(context: ApiContext, params: PickerBrowseParams) {
  const searchParams = new URLSearchParams()
  if (params.query) {
    searchParams.append("query", params.query)
  } else if (params.folderId) {
    searchParams.append("folder_id", params.folderId)
  }

  const query = searchParams.toString()
  const url = `storage_integrations/${params.integrationId}/picker_items${query ? `?${query}` : ""}`

  return apiContextFetch<BrowseResponse>(context, url, { signal: params.signal })
}

export function getStorageIntegrationAccessToken(context: ApiContext, integrationId: string) {
  return apiContextFetch<{ access_token: string | null }>(context, `storage_integrations/${integrationId}/access_token`)
}
